import React, { useState, useEffect } from 'react';
import { getAccessHistory, AccessLogEntry, AccessType } from '../lib/services/AccessLoggerService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { RefreshCw, LogIn, LogOut, Database, FileText, Settings, Key, Trash2 } from 'lucide-react';

const ConnectionHistory: React.FC = () => {
  const [history, setHistory] = useState<AccessLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 20;

  // Charger l'historique
  const loadHistory = async (refresh = false) => {
    try {
      if (refresh) {
        setPage(0);
        setHasMore(true);
        setRefreshing(true);
      } else if (!refresh && !hasMore) {
        return;
      } else {
        setLoading(true);
      }

      const currentPage = refresh ? 0 : page;
      const offset = currentPage * ITEMS_PER_PAGE;
      
      const data = await getAccessHistory(ITEMS_PER_PAGE, offset);
      
      if (data.length < ITEMS_PER_PAGE) {
        setHasMore(false);
      }
      
      if (refresh || currentPage === 0) {
        setHistory(data);
      } else {
        setHistory(prev => [...prev, ...data]);
      }
      
      if (!refresh) {
        setPage(currentPage + 1);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Charger au montage
  useEffect(() => {
    loadHistory();
  }, []);

  // Fonction pour rafraîchir
  const onRefresh = () => {
    loadHistory(true);
  };

  // Fonction pour charger plus
  const loadMore = () => {
    if (!loading && hasMore) {
      loadHistory();
    }
  };

  // Fonction pour formater la date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd MMMM yyyy à HH:mm', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  // Fonction pour obtenir l'icône en fonction du type d'accès
  const getAccessIcon = (accessType: AccessType) => {
    switch (accessType) {
      case AccessType.LOGIN:
        return <LogIn className="w-4 h-4 text-green-400" />;
      case AccessType.LOGOUT:
        return <LogOut className="w-4 h-4 text-red-400" />;
      case AccessType.DATA_ACCESS:
        return <Database className="w-4 h-4 text-blue-400" />;
      case AccessType.DATA_MODIFICATION:
        return <FileText className="w-4 h-4 text-yellow-400" />;
      case AccessType.SETTINGS_CHANGE:
        return <Settings className="w-4 h-4 text-purple-400" />;
      case AccessType.PASSWORD_CHANGE:
        return <Key className="w-4 h-4 text-orange-400" />;
      case AccessType.ACCOUNT_DELETION:
        return <Trash2 className="w-4 h-4 text-red-500" />;
      default:
        return <Database className="w-4 h-4 text-gray-400" />;
    }
  };

  // Fonction pour obtenir le libellé du type d'accès
  const getAccessTypeLabel = (accessType: AccessType) => {
    const labels: Record<AccessType, string> = {
      [AccessType.LOGIN]: 'Connexion',
      [AccessType.LOGOUT]: 'Déconnexion',
      [AccessType.DATA_ACCESS]: 'Accès aux données',
      [AccessType.DATA_MODIFICATION]: 'Modification des données',
      [AccessType.EXPORT]: 'Export',
      [AccessType.IMPORT]: 'Import',
      [AccessType.SETTINGS_CHANGE]: 'Modification des paramètres',
      [AccessType.PASSWORD_CHANGE]: 'Changement de mot de passe',
      [AccessType.ACCOUNT_DELETION]: 'Suppression du compte',
    };
    return labels[accessType] || accessType;
  };

  if (loading && history.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-white text-sm mt-3">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* En-tête avec bouton de rafraîchissement */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white mb-1">Historique des connexions</h2>
          <p className="text-dark-400 text-sm">
            Consultez l'historique de vos connexions et actions
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 px-3 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="text-sm">Actualiser</span>
        </button>
      </div>

      {/* Liste de l'historique */}
      {history.length === 0 ? (
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-8 text-center">
          <Database className="w-12 h-12 text-dark-500 mx-auto mb-4" />
          <h3 className="text-base font-medium text-white mb-2">
            Aucun historique disponible
          </h3>
          <p className="text-dark-400 text-sm">
            Votre historique de connexions apparaîtra ici
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {history.map((entry) => (
            <div
              key={entry.id}
              className="bg-dark-800 border border-dark-700 rounded-lg p-4 hover:border-dark-600 transition-colors"
            >
              <div className="flex items-start space-x-3">
                {/* Icône */}
                <div className="flex-shrink-0 mt-1">
                  {getAccessIcon(entry.access_type)}
                </div>

                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-white font-medium text-sm">
                      {getAccessTypeLabel(entry.access_type)}
                    </h3>
                    <span className="text-dark-400 text-xs">
                      {formatDate(entry.timestamp)}
                    </span>
                  </div>

                  {entry.description && (
                    <p className="text-dark-400 text-sm mb-2">
                      {entry.description}
                    </p>
                  )}

                  {/* Informations sur l'appareil */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {entry.device_info.browser && (
                      <span className="inline-flex items-center px-2 py-1 bg-dark-700 rounded text-xs text-dark-300">
                        {entry.device_info.browser} {entry.device_info.browser_version}
                      </span>
                    )}
                    {entry.device_info.os && (
                      <span className="inline-flex items-center px-2 py-1 bg-dark-700 rounded text-xs text-dark-300">
                        {entry.device_info.os} {entry.device_info.os_version}
                      </span>
                    )}
                    {entry.ip_address && (
                      <span className="inline-flex items-center px-2 py-1 bg-dark-700 rounded text-xs text-dark-300">
                        IP: {entry.ip_address}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Bouton pour charger plus */}
          {hasMore && (
            <div className="text-center pt-4">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm"
              >
                {loading ? 'Chargement...' : 'Charger plus'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectionHistory;

